/**
 * **The colour-wand**: a point pressed on the wall's photo, grown into the
 * contiguous patch of the same colour around it, traced into an outline a
 * setter can add to the wall as a hold (`POST …/climb_scans/:id/holds`).
 *
 * In the browser rather than on the server because the tolerance is a slider:
 * every step of it is a new patch, and it has to follow the hand. The photo is
 * read once, at WORK_PX, into Lab — where a distance between two colours is
 * roughly the difference an eye sees, which is what "the same colour" means
 * here. The server measures the added hold's colour itself, as for any other.
 */

/**
 * **How much a difference in lightness counts against one in hue**, in the
 * distance the patch is grown by, for a hold that has a hue. A hold is shaded:
 * the side away from the light is the same plastic, darker — a change in L far
 * more than in a or b. Let L count fully and the patch stops at the shadow
 * line; at half it follows the plastic round.
 *
 * **Not for a hold without one** (chroma under NEUTRAL_CHROMA): a black, white
 * or grey hold differs from a black volume or a pale wall *only* in lightness,
 * and halving it let a dark green hold on scan 18 spread over the whole black
 * volume under it.
 */
export const LIGHTNESS_WEIGHT = 0.5;
export const NEUTRAL_CHROMA = 20;

/** The longest side the photo is read at: a hold is still dozens of pixels. */
export const WORK_PX = 1600;

/** The tolerance slider, in ΔE76: a few units is "identical", 40 is "similar". */
export const WAND_TOLERANCE_MIN = 4;
export const WAND_TOLERANCE_MAX = 40;
export const WAND_TOLERANCE_DEFAULT = 14;

/**
 * Past this share of the photo the patch is not a hold any more but the wall,
 * a volume or the mats, and growing it further is only time spent: it stops
 * and says so, and the setter lowers the tolerance.
 */
export const MAX_REGION_SHARE = 0.03;

/** Under this many pixels a patch is a speck, not a hold. */
export const MIN_REGION_PX = 12;

/** The most points an outline may have — the same cap a stored hold has. */
export const MAX_OUTLINE_POINTS = 64;

/** The photo as the wand reads it: `lab` is three floats per pixel, row by row. */
export interface WallPixels {
  width: number;
  height: number;
  lab: Float32Array;
}

export type OutlinePoint = [number, number];

export interface WandResult {
  /** The patch's outline in fractions of the photo, or null when there is none. */
  outline: OutlinePoint[] | null;
  /** The patch ran past MAX_REGION_SHARE. */
  overflow: boolean;
  /** The patch was smaller than MIN_REGION_PX. */
  tooSmall: boolean;
}

/**
 * The photo at `url`, read into Lab at no more than `maxSide` pixels a side.
 *
 * Fetched into a blob first, for the reason the crop is (see
 * `cropStoredImage`): a canvas that has drawn a picture from another origin
 * refuses to give its pixels back. `createImageBitmap` honours the EXIF
 * orientation, as the <img> the setter is pointing at does.
 */
export async function loadWallPixels(url: string, maxSide = WORK_PX): Promise<WallPixels> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`The photo could not be read (${response.status})`);

  const bitmap = await createImageBitmap(await response.blob());
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('The photo could not be read');

  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  return { width, height, lab: rgbaToLab(context.getImageData(0, 0, width, height).data) };
}

// sRGB to linear light, once per channel value rather than once per pixel.
const LINEAR = Array.from({ length: 256 }, (_, value) => {
  const c = value / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
});

function labF(t: number): number {
  return t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116;
}

/** RGBA bytes (a canvas's ImageData) as Lab, D65 — three floats per pixel. */
export function rgbaToLab(rgba: Uint8ClampedArray | Uint8Array): Float32Array {
  const pixels = rgba.length / 4;
  const lab = new Float32Array(pixels * 3);

  for (let pixel = 0; pixel < pixels; pixel++) {
    const r = LINEAR[rgba[pixel * 4]];
    const g = LINEAR[rgba[pixel * 4 + 1]];
    const b = LINEAR[rgba[pixel * 4 + 2]];

    const fx = labF((r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047);
    const fy = labF(r * 0.2126 + g * 0.7152 + b * 0.0722);
    const fz = labF((r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883);

    lab[pixel * 3] = 116 * fy - 16;
    lab[pixel * 3 + 1] = 500 * (fx - fy);
    lab[pixel * 3 + 2] = 200 * (fy - fz);
  }

  return lab;
}

/**
 * The patch around `point` (fractions of the photo) and its outline.
 *
 * **Measured against the colour pressed, not against each neighbour**: a
 * gradient — the shade across a sloper — would otherwise lead the fill, a
 * little at a time, out onto the wall. The colour pressed is the mean of a
 * few pixels round the point, so the one pixel under the pointer being a
 * speck of chalk does not decide it.
 */
export function wandOutline(pixels: WallPixels, point: { x: number; y: number }, tolerance: number): WandResult {
  const region = regionAt(pixels, point, tolerance);
  if (region.overflow) return { outline: null, overflow: true, tooSmall: false };
  if (region.count < MIN_REGION_PX) return { outline: null, overflow: false, tooSmall: true };

  const traced = traceBoundary(region.mask, pixels.width, pixels.height);
  const outline = simplifyTo(traced, MAX_OUTLINE_POINTS).map(
    ([x, y]): OutlinePoint => [(x + 0.5) / pixels.width, (y + 0.5) / pixels.height]
  );

  return outline.length >= 3
    ? { outline, overflow: false, tooSmall: false }
    : { outline: null, overflow: false, tooSmall: true };
}

/** The pixels within `tolerance` of the colour pressed, joined to it (4-connected). */
export function regionAt(
  { width, height, lab }: WallPixels,
  point: { x: number; y: number },
  tolerance: number
): { mask: Uint8Array; count: number; overflow: boolean } {
  const mask = new Uint8Array(width * height);
  const sx = Math.min(width - 1, Math.max(0, Math.floor(point.x * width)));
  const sy = Math.min(height - 1, Math.max(0, Math.floor(point.y * height)));
  const [l0, a0, b0] = seedColour(lab, width, height, sx, sy);
  const limit = tolerance * tolerance;
  const lightness = Math.hypot(a0, b0) < NEUTRAL_CHROMA ? 1 : LIGHTNESS_WEIGHT;
  const cap = Math.max(MIN_REGION_PX, Math.floor(width * height * MAX_REGION_SHARE));

  const near = (index: number) => {
    const dl = (lab[index * 3] - l0) * lightness;
    const da = lab[index * 3 + 1] - a0;
    const db = lab[index * 3 + 2] - b0;
    return dl * dl + da * da + db * db <= limit;
  };

  const queue = new Int32Array(cap + 4 * width);
  let head = 0;
  let tail = 0;
  let count = 0;
  // The pixel pressed may itself be the bolt the median looked past: start
  // from the nearest pixel of the colour within the window instead.
  const seed = nearestNear(sx, sy, width, height, near);
  if (seed < 0) return { mask, count: 0, overflow: false };

  mask[seed] = 1;
  queue[tail++] = seed;

  while (head < tail) {
    const index = queue[head++];
    count++;
    if (count > cap) return { mask, count, overflow: true };

    const x = index % width;
    const neighbours = [
      x > 0 ? index - 1 : -1,
      x < width - 1 ? index + 1 : -1,
      index >= width ? index - width : -1,
      index + width < width * height ? index + width : -1,
    ];
    for (const next of neighbours) {
      if (next < 0 || mask[next] === 1 || !near(next)) continue;
      if (tail >= queue.length) return { mask, count, overflow: true };

      mask[next] = 1;
      queue[tail++] = next;
    }
  }

  return { mask, count, overflow: false };
}

/**
 * The colour pressed: the median, channel by channel, of the 7x7 pixels round
 * the point. The median because the middle of a hold is where its bolt is — a
 * dark hole a mean would drag the colour towards.
 */
function seedColour(lab: Float32Array, width: number, height: number, sx: number, sy: number) {
  const channels: number[][] = [[], [], []];
  for (let y = Math.max(0, sy - 3); y <= Math.min(height - 1, sy + 3); y++) {
    for (let x = Math.max(0, sx - 3); x <= Math.min(width - 1, sx + 3); x++) {
      const index = (y * width + x) * 3;
      for (let band = 0; band < 3; band++) channels[band].push(lab[index + band]);
    }
  }

  return channels.map((values) => {
    values.sort((a, b) => a - b);
    return values[Math.floor(values.length / 2)];
  });
}

function nearestNear(
  sx: number,
  sy: number,
  width: number,
  height: number,
  near: (index: number) => boolean
): number {
  for (let radius = 0; radius <= 3; radius++) {
    for (let y = sy - radius; y <= sy + radius; y++) {
      for (let x = sx - radius; x <= sx + radius; x++) {
        if (x < 0 || y < 0 || x >= width || y >= height) continue;
        if (Math.max(Math.abs(x - sx), Math.abs(y - sy)) !== radius) continue;
        if (near(y * width + x)) return y * width + x;
      }
    }
  }

  return -1;
}

// The eight neighbours, clockwise from the west (y grows downwards).
const RING: Array<[number, number]> = [
  [-1, 0],
  [-1, -1],
  [0, -1],
  [1, -1],
  [1, 0],
  [1, 1],
  [0, 1],
  [-1, 1],
];

/**
 * **The patch's outer edge**, as the pixels along it in order (Moore-neighbour
 * tracing, stopped by Jacob's criterion: back at the start, about to take the
 * first step again). Only the outside: a highlight inside a hold leaves a hole
 * in the patch, and the hold is still the shape around it.
 */
export function traceBoundary(mask: Uint8Array, width: number, height: number): OutlinePoint[] {
  const start = mask.indexOf(1);
  if (start < 0) return [];

  const sx = start % width;
  const sy = Math.floor(start / width);
  const inside = (x: number, y: number) =>
    x >= 0 && y >= 0 && x < width && y < height && mask[y * width + x] === 1;

  const points: OutlinePoint[] = [[sx, sy]];
  let cx = sx;
  let cy = sy;
  // The first pixel in reading order: everything west of it is outside.
  let back = 0;
  let firstMove = -1;

  for (let guard = 0; guard < 4 * width * height + 8; guard++) {
    let moved = -1;
    for (let k = 1; k <= 8; k++) {
      const direction = (back + k) % 8;
      if (inside(cx + RING[direction][0], cy + RING[direction][1])) {
        moved = direction;
        break;
      }
    }
    if (moved < 0) break; // a single pixel

    if (cx === sx && cy === sy) {
      if (firstMove === -1) firstMove = moved;
      else if (moved === firstMove) break;
    }

    // The neighbour looked at just before the one moved to was outside: from
    // the new pixel, that is where the next look starts.
    const outside = (moved + 7) % 8;
    const bx = cx + RING[outside][0];
    const by = cy + RING[outside][1];
    cx += RING[moved][0];
    cy += RING[moved][1];
    back = RING.findIndex(([dx, dy]) => dx === bx - cx && dy === by - cy);
    points.push([cx, cy]);
  }

  const last = points[points.length - 1];
  if (points.length > 1 && last[0] === sx && last[1] === sy) points.pop();
  return points;
}

/** Douglas-Peucker, loosened until the outline fits in `maxPoints`. */
export function simplifyTo(points: OutlinePoint[], maxPoints: number): OutlinePoint[] {
  if (points.length <= maxPoints) return points;

  let epsilon = 0.75;
  let simplified = simplifyClosed(points, epsilon);
  while (simplified.length > maxPoints) {
    epsilon *= 1.5;
    simplified = simplifyClosed(points, epsilon);
  }

  return simplified;
}

// A closed outline split at its two farthest-apart points, each half
// simplified as an open line.
function simplifyClosed(points: OutlinePoint[], epsilon: number): OutlinePoint[] {
  let far = 0;
  let farthest = -1;
  for (let index = 1; index < points.length; index++) {
    const d = (points[index][0] - points[0][0]) ** 2 + (points[index][1] - points[0][1]) ** 2;
    if (d > farthest) {
      farthest = d;
      far = index;
    }
  }

  const first = simplifyOpen(points.slice(0, far + 1), epsilon);
  const second = simplifyOpen([...points.slice(far), points[0]], epsilon);

  return [...first.slice(0, -1), ...second.slice(0, -1)];
}

function simplifyOpen(points: OutlinePoint[], epsilon: number): OutlinePoint[] {
  if (points.length < 3) return points;

  const [ax, ay] = points[0];
  const [bx, by] = points[points.length - 1];
  const length = Math.hypot(bx - ax, by - ay);
  let worst = 0;
  let at = 0;
  for (let index = 1; index < points.length - 1; index++) {
    const [px, py] = points[index];
    const distance =
      length === 0
        ? Math.hypot(px - ax, py - ay)
        : Math.abs((bx - ax) * (ay - py) - (ax - px) * (by - ay)) / length;
    if (distance > worst) {
      worst = distance;
      at = index;
    }
  }

  if (worst <= epsilon) return [points[0], points[points.length - 1]];

  return [
    ...simplifyOpen(points.slice(0, at + 1), epsilon).slice(0, -1),
    ...simplifyOpen(points.slice(at), epsilon),
  ];
}
